import { useRef, type KeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, RequestError } from '../lib/api.js';
import { currentLocale } from '../i18n.js';
import { formatRelative } from '../lib/format.js';
import { Button, ErrorNotice } from '../components/ui.js';
import type { ShellNotification } from '../components/shell/TopBar.js';
import { ActivityPage } from './ActivityPage.js';
import { cn } from '../lib/cn.js';

type InboxTab = 'notifications' | 'activities';
const tabs: InboxTab[] = ['notifications', 'activities'];
export function notificationTabKey(current: InboxTab, key: string, rtl: boolean): InboxTab | null {
  if (key === 'Home') return 'notifications';
  if (key === 'End') return 'activities';
  if (key !== 'ArrowLeft' && key !== 'ArrowRight') return null;
  const step = (key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1);
  return tabs[(tabs.indexOf(current) + step + tabs.length) % tabs.length] ?? null;
}

/** Read state belongs only to notification rows; audit history stays read-only. */
export function NotificationsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const tab: InboxTab = params.get('tab') === 'activities' ? 'activities' : 'notifications';
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const navigate = useNavigate();
  const client = useQueryClient();
  const locale = currentLocale();
  const list = useQuery({ queryKey: ['notifications'], queryFn: () => api.get<{ items: ShellNotification[]; unread: number }>('/notifications'), enabled: tab === 'notifications' });
  const refresh = () => { void client.invalidateQueries({ queryKey: ['notifications'] }); void client.invalidateQueries({ queryKey: ['dashboard'] }); };
  const read = useMutation({ mutationFn: (id: string) => api.post(`/notifications/${id}/read`), onSuccess: refresh });
  const readAll = useMutation({ mutationFn: () => api.post('/notifications/read-all'), onSuccess: refresh });
  const choose = (next: InboxTab) => {
    const updated = new URLSearchParams(params);
    if (next === 'activities') updated.set('tab', next); else updated.delete('tab');
    setParams(updated, { replace: true });
  };
  const onKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const next = notificationTabKey(tab, event.key, document.documentElement.dir === 'rtl');
    if (!next) return;
    event.preventDefault(); choose(next); refs.current[tabs.indexOf(next)]?.focus();
  };
  const error = list.error ?? read.error ?? readAll.error;
  return <div className="max-w-4xl mx-auto space-y-4">
    <div role="tablist" aria-label={t('notifications.title')} className="flex gap-2 border-b border-border">
      {tabs.map((value, index) => <button key={value} ref={(node) => { refs.current[index] = node; }} type="button" role="tab" id={`notifications-tab-${value}`} aria-selected={tab === value} aria-controls={`notifications-panel-${value}`} tabIndex={tab === value ? 0 : -1} onKeyDown={onKey} onClick={() => choose(value)} className={cn('min-h-11 px-4 py-2 text-sm font-medium border-b-2', tab === value ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground')}>
        {t(value === 'activities' ? 'nav.activity' : 'notifications.title')}
      </button>)}
    </div>
    <section role="tabpanel" id={`notifications-panel-${tab}`} aria-labelledby={`notifications-tab-${tab}`} tabIndex={0}>
      {tab === 'activities' ? <ActivityPage /> : <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link to="/reminders" className="min-h-11 inline-flex items-center text-sm text-primary hover:underline">{t('nav.reminders')}</Link>
          <Button loading={readAll.isPending} disabled={!list.data?.unread} onClick={() => readAll.mutate()}>{t('notifications.markAllRead')}</Button>
        </div>
        {error && <ErrorNotice code={error instanceof RequestError ? error.code : 'errors.unknown'} />}
        {list.isPending && <p role="status">{t('common.loading')}</p>}
        {list.isSuccess && list.data.items.length === 0 && <p className="p-6 text-center text-muted-foreground">{t('notifications.empty')}</p>}
        <ul className="space-y-2">{list.data?.items.map((item) => <li key={item.id}>
          <button type="button" disabled={read.isPending} onClick={() => { if (!item.readAt) read.mutate(item.id); if (item.link) navigate(item.link); }} className={cn('min-h-14 w-full rounded-xl border border-border p-4 text-start hover:border-primary/40', !item.readAt && 'bg-primary/5 font-medium')}>
            <span className="block break-words text-sm">{t(item.title, item.vars)}</span>
            <time dateTime={item.createdAt} className="block mt-1 text-xs font-normal text-muted-foreground">{formatRelative(item.createdAt, locale)}</time>
          </button>
        </li>)}</ul>
      </div>}
    </section>
  </div>;
}
